import * as XLSX from 'xlsx';

/** 좌표 컬럼 후보를 찾아줍니다. (우선순위 포함) */
function detectXYHeaders(headers) {
    const lower = headers.map(h => String(h || '').trim());
    const matchOne = (cands) =>
        lower.findIndex(h => cands.some(rx => rx.test(h)));

    // 후보 정규식 목록 (순서대로 우선순위)
    const X_CANDIDATES = [
        /^(x|lon|lng|경도|x3857|x_3857|x4326|x_4326)$/i,
        /(x.?coord|coord.?x)/i,
        /(경도)/, // 한글
    ];
    const Y_CANDIDATES = [
        /^(y|lat|위도|y3857|y_3857|y4326|y_4326)$/i,
        /(y.?coord|coord.?y)/i,
        /(위도)/,
    ];

    let xi = matchOne(X_CANDIDATES);
    let yi = matchOne(Y_CANDIDATES);

    // x만 찾은 경우/ y만 찾은 경우: 인접열 유추는 위험하므로 패스
    if (xi === -1 || yi === -1) return { xi: -1, yi: -1 };
    return { xi, yi };
}

/** 숫자 판별 */
const num = (v) => {
    const n = typeof v === 'string' ? v.replace(/,/g, '').trim() : v;
    const f = Number(n);
    return Number.isFinite(f) ? f : null;
};

/**
 * Blob(CSV/XLS/XLSX) → GeoJSON(Point) 변환
 * - 좌표 컬럼 자동탐지 (x/y, lon/lat, 경도/위도, x3857/y3857 등)
 * - 좌표 숫자인 행만 Feature 생성
 * - 빈 결과면 빈 FC 반환
 */
export async function blobToGeoJSON(blob/*, hintFormat */) {
    if (!blob) return { type: 'FeatureCollection', features: [] };

    let wb;
    try {
        // 1) 워크북 파싱 시도 (arraybuffer → XLSX.read)
        try {
            const ab = await blob.arrayBuffer();
            wb = XLSX.read(ab, { type: 'array' });
        } catch (e) {
            const text = await blob.text();
            wb = XLSX.read(text, { type: 'string', raw: true });
        }

        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const rows2d = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (!rows2d || rows2d.length < 2) {
            return { type: 'FeatureCollection', features: [] };
        }

        const headers = rows2d[0].map(h => String(h ?? '').trim());
        const { xi, yi } = detectXYHeaders(headers);
        if (xi < 0 || yi < 0) {
            // 좌표 컬럼을 못 찾으면 빈 FC
            return { type: 'FeatureCollection', features: [] };
        }

        const features = [];
        for (let i = 1; i < rows2d.length; i++) {
            const row = rows2d[i];
            const X = num(row[xi]);
            const Y = num(row[yi]);
            if (X == null || Y == null) continue;

            // properties 구성
            const props = {};
            headers.forEach((h, idx) => {
                props[h] = row[idx] ?? '';
            });

            features.push({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [X, Y] },
                properties: props,
            });
        }

        return { type: 'FeatureCollection', features };
    } catch (e) {
        console.error("Failed to parse blob as Excel or CSV.", e);
        return { type: 'FeatureCollection', features: [] };
    }
}

/** (참고) XY 스왑 필요시 */
export function swapXYGeoJSON(gj) {
    if (!gj) return gj;
    const clone = JSON.parse(JSON.stringify(gj));
    const swap = (c) => [c[1], c[0]];

    for (const f of clone.features || []) {
        if (!f.geometry) continue;
        const g = f.geometry;
        if (g.type === 'Point') g.coordinates = swap(g.coordinates);
        else if (g.type === 'MultiPoint' || g.type === 'LineString') g.coordinates = g.coordinates.map(swap);
        else if (g.type === 'MultiLineString' || g.type === 'Polygon') g.coordinates = g.coordinates.map(r => r.map(swap));
        else if (g.type === 'MultiPolygon') g.coordinates = g.coordinates.map(p => p.map(r => r.map(swap)));
    }
    return clone;
}
